import * as Sentry from "@sentry/browser";
import { Spin, message } from "antd";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import React from "react";
import BaseForm from "../../components/Form/BaseForm";
import StepContainer from "../../components/StepContainer";
import { APIError, Opportunity } from "../../helpers/shared-interfaces";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../../helpers/utilities";
import { ASSIGN_PHONE_NUMBER_TO_OPPORTUNITY } from "../../gql/opportunity";

interface Props {
  prev: () => void;
  next: () => void;
  setPercentage: (percentage: number) => void;
  getNextViewName: () => string | null;
  getPreviousViewName: () => string | null;

  opportunity: Opportunity;
}

interface State {
  isLoading: boolean;
}

export default class PhoneNumberForm extends React.Component<Props, State> {
  reference = React.createRef<FormInstance>();

  constructor(props: Readonly<Props>) {
    super(props);
    this.state = { isLoading: true };
    this.assignPhoneNumberToOpportunity =
      this.assignPhoneNumberToOpportunity.bind(this);
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  componentDidMount(): void {
    this.props.setPercentage(40);
    this.setState({ isLoading: false });
  }

  assignPhoneNumberToOpportunity<T>(
    phoneNumber: string,
  ): Promise<AxiosResponse<T>> {
    const { opportunity } = this.props;
    return request({
      query: ASSIGN_PHONE_NUMBER_TO_OPPORTUNITY,
      variables: {
        opportunityId: opportunity.id,
        phoneNumber,
      },
    });
  }

  next = async (): Promise<void> => {
    const fieldsToBeValidated = ["phoneNumber"];
    
    interface Response {
      data: {
        assignPhoneToOpportunity: {
          opportunity: Opportunity;
          errors: APIError[];
        };
      };
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    const assignMutation = this.assignPhoneNumberToOpportunity;

    try {
      const response: AxiosResponse<Response> = await assignMutation<Response>(
        data.phoneNumber,
      );

      const { err, handled } = responseHandler<Response>(
        response,
        "assignPhoneToOpportunity",
      );

      this.setState({ isLoading: false });

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
        return;
      }

      if (
        response.data.data.assignPhoneToOpportunity.opportunity.customer ===
        null
      ) {
        message.error("An Error Occurred. Phone number could not be assigned.");
        return;
      } else {
        this.props.next();
      }
    } catch (error) {
      this.setState({ isLoading: false });

      message.error(`Request Failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  render(): JSX.Element {
    const { opportunity } = this.props;
    const { isLoading } = this.state;

    const customer = opportunity.customer;

    let phone = undefined;

    customer &&
      customer?.activeCustomerPhones?.length > 0 &&
      (phone = customer?.activeCustomerPhones[0]?.phone.number);

    return (
      <Spin spinning={isLoading}>
        <StepContainer
          getNextViewName={this.props.getNextViewName}
          getPreviousViewName={this.props.getPreviousViewName}
          style={{
            position: "relative",
            top: "-10px",
          }}
          config={{
            prev: this.props.prev,
            next: this.next,
            showNext: true,
            showPrev: true,
          }}
        >
          <BaseForm
            reference={this.reference}
            layout="vertical"
            fieldsToHide={[]}
            fields={[
              {
                id: "phoneNumber",
                name: "phoneNumber",
                inputType: "phone",
                rule: "string",
                label: "Phone",
                placeholder: "Phone",
                initialValue: phone,
                required: false,
                colConfig: {
                  span: 11,
                },
              },
            ]}
          />
        </StepContainer>
      </Spin>
    );
  }
}
